import { useQuery, useMutation, useQueryClient } from "react-query";
import { fetchData } from "../api/client";

/* ===== Util ===== */
const qs = (params) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
};

export const useCompanies = (page = 1, limit = 10, search = "") => {
  const queryClient = useQueryClient();

  const queryKey = ["companies", { page, limit, search }];

  const query = useQuery(
    queryKey,
    async () => {
      const url = `/companies${qs({ page, limit, search })}`;
      const resp = await fetchData(url);
      const companies = Array.isArray(resp?.data) ? resp.data : [];
      const pagination =
        resp?.pagination || { total: 0, page: Number(page) || 1, limit: Number(limit) || 10 };
      return { companies, pagination };
    },
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = error?.response?.status || error?.status;
        if (status === 401 || status === 403) return false;
        return failureCount < 2;
      },
    }
  );

  /**
   * GET /companies/:id
   * API retorna: { message, data: { projeto, regioes, cepRanges } }
   * Normalizamos para { projeto: {...}, regioes: [...], cepRanges: [...] }
   */
  const getById = async (id) => {
    if (!id) throw new Error("getById requer um ID válido");
    const resp = await fetchData(`/companies/${id}`, "GET");

    // A API embrulha tudo em `data`
    const payload = resp?.data ?? resp ?? {};

    return {
      projeto: payload.projeto || {},
      regioes: Array.isArray(payload.regioes) ? payload.regioes : [],
      cepRanges: Array.isArray(payload.cepRanges) ? payload.cepRanges : [],
    };
  };

  // POST/PUT
  const updateMutation = useMutation(
    async (company) => {
      const idFromProjeto = company?.projeto?.id;
      const idFallback = company?.id;
      const id = idFromProjeto ?? idFallback;
      const method = id ? "PUT" : "POST";
      const endpoint = id ? `/companies/${id}` : `/companies`;
      return fetchData(endpoint, method, company);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["companies"] });
      },
    }
  );

  const deleteMutation = useMutation(
    async (id) => {
      if (!id) throw new Error("deleteMutation requer um ID");
      return fetchData(`/companies/${id}`, "DELETE");
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["companies"] });
      },
    }
  );

  return { ...query, updateMutation, deleteMutation, getById };
};
